import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useLanguage } from "../contexts/LanguageContext";
import CustomerLayout from "../components/CustomerLayout";
import DataPagination from "../components/DataPagination";
import api from "../lib/api";
import { toast } from "sonner";
import { 
  Globe, CheckCircle,
  Link2, ChevronDown, ChevronRight,
  Trash2, Ban, Edit, Save, XCircle, Key
} from "lucide-react";

const ITEMS_PER_PAGE = 20;

const MyLinks = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [groupedLinks, setGroupedLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // İlk yükleme kontrolü
  const [expandedUrls, setExpandedUrls] = useState({});
  const [linkDetails, setLinkDetails] = useState({});
  const [deletingLinks, setDeletingLinks] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalLinks, setTotalLinks] = useState(0);
  const [activeLinks, setActiveLinks] = useState(0);
  
  // Expanded links pagination & search state
  const [expandedLinksPage, setExpandedLinksPage] = useState({});
  const [expandedLinksSearch, setExpandedLinksSearch] = useState({});
  const [expandedLinksTotalPages, setExpandedLinksTotalPages] = useState({});
  const LINKS_PER_PAGE = 20;
  
  // Toplu işlem state'leri
  const [bulkActionLoading, setBulkActionLoading] = useState({});
  const [editingUrl, setEditingUrl] = useState(null);
  const [newTargetUrl, setNewTargetUrl] = useState("");
  
  // Keyword editing state (single)
  const [editingKeyword, setEditingKeyword] = useState(null); // {orderId, keyword}
  const [newKeyword, setNewKeyword] = useState("");
  const [savingKeyword, setSavingKeyword] = useState(false);
  
  // Bulk keyword editing state
  const [editingBulkKeyword, setEditingBulkKeyword] = useState(null); // target_url being edited
  const [selectedBulkOldKeyword, setSelectedBulkOldKeyword] = useState(""); // hangi keyword değiştirilecek
  const [newBulkKeyword, setNewBulkKeyword] = useState("");
  
  // Failed/refund system removed - all orders shown as active to customers

  // İlk yükleme; ?expand=url ile gelindiyse o hedef açık ve ilk sayfa yüklü
  useEffect(() => {
    let isMounted = true;
    const expandUrl = searchParams.get("expand");

    const initData = async () => {
      try {
        const params = new URLSearchParams({ page: 1, limit: ITEMS_PER_PAGE });
        if (expandUrl) params.append("search", expandUrl);
        const linksRes = await api.get(`/my-links?${params}`);

        if (isMounted) {
          setGroupedLinks(linksRes.data.grouped_links || []);
          setTotalPages(linksRes.data.pages || 1);
          setTotalItems(linksRes.data.total_target_sites || 0);
          setTotalLinks(linksRes.data.total_links || 0);
          setActiveLinks(linksRes.data.total_active || 0);
          if (expandUrl) {
            setSearchQuery(expandUrl);
            setCurrentPage(1);
            setExpandedUrls(prev => ({ ...prev, [expandUrl]: true }));
            setExpandedLinksPage(prev => ({ ...prev, [expandUrl]: 1 }));
            setExpandedLinksSearch(prev => ({ ...prev, [expandUrl]: "" }));
            try {
              const detailParams = new URLSearchParams({ page: 1, limit: LINKS_PER_PAGE });
              const detailRes = await api.get(`/my-links/${encodeURIComponent(expandUrl)}?${detailParams}`);
              const links = detailRes.data.links || [];
              const totalPages = detailRes.data.pages || 1;
              const totalCount = detailRes.data.detail_total ?? detailRes.data.total_links ?? links.length;
              setExpandedLinksTotalPages(prev => ({ ...prev, [expandUrl]: totalPages }));
              setLinkDetails(prev => ({ ...prev, [expandUrl]: links, [`${expandUrl}_total`]: totalCount }));
            } catch (e) {
              console.error("Expand link details error:", e);
            }
          }
          setIsInitialized(true);
        }
      } catch (error) {
        console.error("Initial data fetch error:", error);
        if (isMounted) {
          setIsInitialized(true);
        }
      }
    };

    initData();
    return () => { isMounted = false; };
  }, [searchParams]);

  // Sayfa değişikliği veya arama için fetch
  const fetchMyLinks = useCallback(async (page = 1, search = "") => {
    if (!isInitialized) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: ITEMS_PER_PAGE });
      if (search) params.append("search", search);
      const response = await api.get(`/my-links?${params}`);
      
      setGroupedLinks(response.data.grouped_links || []);
      setTotalPages(response.data.pages || 1);
      setTotalItems(response.data.total_target_sites || 0);
      setTotalLinks(response.data.total_links || 0);
      setActiveLinks(response.data.total_active || 0);
    } catch (error) {
      console.error("Linkler yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  }, [isInitialized]);

  // Failed/refund functions removed - all orders shown as active

  // Search with debounce - only after initialization and when search actually changes
  useEffect(() => {
    if (!isInitialized) return;
    
    // Skip if this is the initial render (searchQuery is empty)
    const timer = setTimeout(() => {
      if (searchQuery !== "") {
        fetchMyLinks(1, searchQuery);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchMyLinks, isInitialized]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchMyLinks(page, searchQuery);
  };

  const toggleExpand = async (targetUrl) => {
    const isExpanded = expandedUrls[targetUrl];
    setExpandedUrls(prev => ({ ...prev, [targetUrl]: !isExpanded }));
    
    // Eğer daha önce detaylar yüklenmediyse yükle
    if (!isExpanded && !linkDetails[targetUrl]) {
      // İlk sayfa ve arama state'ini başlat
      setExpandedLinksPage(prev => ({ ...prev, [targetUrl]: 1 }));
      setExpandedLinksSearch(prev => ({ ...prev, [targetUrl]: "" }));
      await fetchExpandedLinks(targetUrl, 1, "");
    }
  };

  // Genişletilmiş linkler için fetch fonksiyonu
  const fetchExpandedLinks = async (targetUrl, page = 1, search = "") => {
    try {
      const params = new URLSearchParams({ page, limit: LINKS_PER_PAGE });
      if (search.trim()) params.append("search", search.trim());
      const response = await api.get(`/my-links/${encodeURIComponent(targetUrl)}?${params}`);
      const links = response.data.links || [];
      const totalPages = response.data.pages || 1;
      const totalCount = response.data.detail_total ?? response.data.total_links ?? links.length;
      setExpandedLinksTotalPages(prev => ({ ...prev, [targetUrl]: totalPages }));
      setLinkDetails(prev => ({ 
        ...prev, 
        [targetUrl]: links,
        [`${targetUrl}_total`]: totalCount
      }));
    } catch (error) {
      console.error("Link detayları yüklenemedi:", error);
    }
  };

  // Genişletilmiş linkler sayfa değişimi
  const handleExpandedPageChange = async (targetUrl, newPage) => {
    setExpandedLinksPage(prev => ({ ...prev, [targetUrl]: newPage }));
    const search = expandedLinksSearch[targetUrl] || "";
    await fetchExpandedLinks(targetUrl, newPage, search);
  };

  // Genişletilmiş linkler arama
  const handleExpandedSearchChange = async (targetUrl, searchValue) => {
    setExpandedLinksSearch(prev => ({ ...prev, [targetUrl]: searchValue }));
    // Debounce
    if (window[`searchTimeout_${targetUrl}`]) {
      clearTimeout(window[`searchTimeout_${targetUrl}`]);
    }
    window[`searchTimeout_${targetUrl}`] = setTimeout(async () => {
      setExpandedLinksPage(prev => ({ ...prev, [targetUrl]: 1 }));
      await fetchExpandedLinks(targetUrl, 1, searchValue);
    }, 300);
  };

  // Toplu iptal = KÖKTEN SİL. Buton basınca grup UI'dan anında gider.
  const bulkCancel = async (targetUrl) => {
    const ok = window.confirm(
      language === "tr"
        ? `"${targetUrl}" hedefindeki tüm linkler silinecek. Devam?`
        : `All links for "${targetUrl}" will be deleted. Continue?`
    );
    if (!ok) return;

    // Optimistic UI: grubu listeden ANINDA çıkar
    setGroupedLinks(prev => prev.filter(g => g.target_url !== targetUrl));
    setLinkDetails(prev => {
      const copy = { ...prev };
      delete copy[targetUrl];
      return copy;
    });
    toast.success(language === "tr" ? "Linkler silindi" : "Links deleted");

    // Fire-and-forget — backend hemen 202 döner, silmeyi arka planda yapar
    api.post("/my-links/bulk-action", { target_url: targetUrl, action: "cancel" })
      .catch(() => { /* sessiz, müşteri optimistic UI'yı zaten gördü */ });
  };

  // Toplu silme (cancelled olanları kaldır) — aynı pattern, sessiz fire-and-forget
  const bulkDelete = async (targetUrl) => {
    const ok = window.confirm(
      language === "tr"
        ? `"${targetUrl}" hedefindeki tüm linkler listeden silinecek. Devam?`
        : `All links for "${targetUrl}" will be removed. Continue?`
    );
    if (!ok) return;

    setGroupedLinks(prev => prev.filter(g => g.target_url !== targetUrl));
    setLinkDetails(prev => {
      const copy = { ...prev };
      delete copy[targetUrl];
      return copy;
    });
    toast.success(language === "tr" ? "Linkler silindi" : "Links deleted");

    api.post("/my-links/bulk-action", { target_url: targetUrl, action: "delete" })
      .catch(() => {});
  };

  // Hedef URL değiştirme
  const startEditingUrl = (targetUrl) => {
    setEditingUrl(targetUrl);
    setNewTargetUrl(targetUrl);
  };

  const cancelEditingUrl = () => {
    setEditingUrl(null);
    setNewTargetUrl("");
  };

  const saveNewTargetUrl = async (oldUrl) => {
    if (!newTargetUrl || newTargetUrl === oldUrl) {
      cancelEditingUrl();
      return;
    }

    setBulkActionLoading(prev => ({ ...prev, [oldUrl]: "update" }));
    try {
      const res = await api.put(
        "/my-links/update-target",
        {
          old_target_url: oldUrl,
          new_target_url: newTargetUrl,
        },
        { timeout: 120000 }
      );
      if (res.data?.background) {
        toast.success(
          language === "tr"
            ? `${res.data.queued || 0} link için URL değişikliği başlatıldı. Siteler arka planda güncellenir (birkaç dk).`
            : `URL change started for ${res.data.queued || 0} links. Sites update in the background (a few minutes).`
        );
      } else {
        toast.success(
          language === "tr"
            ? `${res.data.updated || 0} link güncellendi`
            : `${res.data.updated || 0} links updated`
        );
        if (res.data.failed > 0) {
          toast.warning(
            language === "tr"
              ? `${res.data.failed} link güncellenemedi`
              : `${res.data.failed} links failed to update`
          );
        }
      }
      await fetchMyLinks(currentPage, searchQuery);
      setLinkDetails({});
      cancelEditingUrl();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        detail ||
          (language === "tr" ? "Güncelleme başarısız" : "Update failed")
      );
    } finally {
      setBulkActionLoading(prev => ({ ...prev, [oldUrl]: null }));
    }
  };

  // Keyword editing functions
  const startEditingKeyword = (orderId, currentKeyword) => {
    setEditingKeyword(orderId);
    setNewKeyword(currentKeyword);
  };

  const cancelEditingKeyword = () => {
    setEditingKeyword(null);
    setNewKeyword("");
  };

  const saveNewKeyword = async (orderId, targetUrl) => {
    if (!newKeyword || newKeyword.trim() === "") {
      toast.error(language === "tr" ? "Keyword boş olamaz" : "Keyword cannot be empty");
      return;
    }
    
    setSavingKeyword(true);
    try {
      await api.put("/my-links/update-keyword", {
        order_id: orderId,
        new_keyword: newKeyword.trim()
      });
      toast.success(language === "tr" ? "Keyword güncellendi" : "Keyword updated");
      
      // Refresh the details for this target URL
      if (linkDetails[targetUrl]) {
        const page = expandedLinksPage[targetUrl] || 1;
        const search = expandedLinksSearch[targetUrl] || "";
        await fetchExpandedLinks(targetUrl, page, search);
      }
      cancelEditingKeyword();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Güncelleme başarısız" : "Update failed"));
    } finally {
      setSavingKeyword(false);
    }
  };

  // Bulk keyword editing functions
  const startEditingBulkKeyword = (targetUrl, keywords = []) => {
    setEditingBulkKeyword(targetUrl);
    setSelectedBulkOldKeyword(keywords && keywords.length > 0 ? keywords[0] : "");
    setNewBulkKeyword("");
  };

  const cancelEditingBulkKeyword = () => {
    setEditingBulkKeyword(null);
    setSelectedBulkOldKeyword("");
    setNewBulkKeyword("");
  };

  const saveBulkKeyword = async (targetUrl) => {
    if (!selectedBulkOldKeyword || selectedBulkOldKeyword.trim() === "") {
      toast.error(language === "tr" ? "Değiştirilecek keyword seçin" : "Select keyword to replace");
      return;
    }
    if (!newBulkKeyword || newBulkKeyword.trim() === "") {
      toast.error(language === "tr" ? "Yeni keyword boş olamaz" : "New keyword cannot be empty");
      return;
    }
    
    setBulkActionLoading(prev => ({ ...prev, [targetUrl]: "bulk-keyword" }));
    try {
      const response = await api.put("/my-links/bulk-update-keyword", {
        target_url: targetUrl,
        old_keyword: selectedBulkOldKeyword.trim(),
        new_keyword: newBulkKeyword.trim()
      });
      
      toast.success(language === "tr" 
        ? `${response.data.updated} link güncelleniyor (arka planda)` 
        : `${response.data.updated} links updating (in background)`);
      
      // Refresh the details for this target URL
      if (linkDetails[targetUrl]) {
        const page = expandedLinksPage[targetUrl] || 1;
        const search = expandedLinksSearch[targetUrl] || "";
        await fetchExpandedLinks(targetUrl, page, search);
      }
      
      // Refresh main list too
      await fetchMyLinks(currentPage, searchQuery);
      cancelEditingBulkKeyword();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Güncelleme başarısız" : "Update failed"));
    } finally {
      setBulkActionLoading(prev => ({ ...prev, [targetUrl]: null }));
    }
  };

  const deleteLink = async (orderId, targetUrl, status) => {
    const ok = window.confirm(
      language === "tr"
        ? "Bu linki kalıcı olarak silmek istiyor musunuz?"
        : "Delete this link permanently?"
    );
    if (!ok) return;

    setDeletingLinks(prev => ({ ...prev, [orderId]: true }));
    
    try {
      await api.delete(`/my-links/${orderId}`);

      setLinkDetails(prev => {
        const currentPageLinks = prev[targetUrl] || [];
        const nextPageLinks = currentPageLinks.filter(link => link.id !== orderId);
        const nextTotal = Math.max(0, (prev[`${targetUrl}_total`] || currentPageLinks.length) - 1);
        return {
          ...prev,
          [targetUrl]: nextPageLinks,
          [`${targetUrl}_total`]: nextTotal,
        };
      });

      setExpandedLinksTotalPages(prev => {
        const nextCount = Math.max(0, (linkDetails[`${targetUrl}_total`] || 1) - 1);
        return {
          ...prev,
          [targetUrl]: Math.max(1, Math.ceil(nextCount / LINKS_PER_PAGE)),
        };
      });

      setGroupedLinks(prev => prev
        .map(group => {
          if (group.target_url !== targetUrl) return group;
          return {
            ...group,
            total_links: Math.max(0, (group.total_links || 0) - 1),
          };
        })
        .filter(group => (group.total_links || 0) > 0)
      );
      setTotalLinks(prev => Math.max(0, prev - 1));
      
      toast.success(language === "tr" ? "Link silindi" : "Link deleted");
      
    } catch (error) {
      console.error("Link silinemedi:", error);
      alert(language === "tr" 
        ? "İşlem sırasında hata oluştu. Lütfen tekrar deneyin." 
        : "An error occurred. Please try again.");
    } finally {
      setDeletingLinks(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const cancelLink = async (orderId, targetUrl) => {
    const ok = window.confirm(
      language === "tr"
        ? "Bu link silinsin mi?"
        : "Delete this link?"
    );
    if (!ok) return;

    // Optimistic: bu order'ı detay listesinden anında çıkar
    setLinkDetails(prev => {
      const currentPageLinks = prev[targetUrl];
      if (!currentPageLinks) return prev;
      return {
        ...prev,
        [targetUrl]: currentPageLinks.filter(l => l.id !== orderId),
        [`${targetUrl}_total`]: Math.max(0, (prev[`${targetUrl}_total`] || currentPageLinks.length) - 1),
      };
    });
    // Group satırının sayılarını anında düşür
    setGroupedLinks(prev => prev.map(g => g.target_url === targetUrl
      ? { ...g, total_links: Math.max(0, (g.total_links || 0) - 1), active_links: Math.max(0, (g.active_links || 0) - 1) }
      : g
    ));
    setTotalLinks(prev => Math.max(0, prev - 1));
    setActiveLinks(prev => Math.max(0, prev - 1));
    toast.success(language === "tr" ? "Link silindi" : "Link deleted");

    api.post(`/my-links/${orderId}/cancel`).catch(() => { /* sessiz */ });
  };

  const getStatusBadge = (status) => {
    // Show cancelled orders as cancelled, others as active
    if (status === "cancelled") {
      return (
        <span className="flex items-center gap-1 text-xs font-mono px-2 py-1 bg-orange-500/20 text-orange-400 border border-orange-500/30">
          <XCircle className="w-3 h-3" />
          {language === "tr" ? "İPTAL" : "CANCELLED"}
        </span>
      );
    }
    // All other orders shown as active to customers
    return (
      <span className="flex items-center gap-1 text-xs font-mono px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30">
        <CheckCircle className="w-3 h-3" />
        {language === "tr" ? "AKTİF" : "ACTIVE"}
      </span>
    );
  };

  return (
    <CustomerLayout activeId="mylinks">
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-mono font-bold uppercase">{language === "tr" ? "Linklerim" : "My Links"}</h1>
            <p className="text-muted-foreground mt-1">
              {language === "tr" ? "Satın aldığınız tüm backlink'leri buradan takip edebilirsiniz" : "Track all your purchased backlinks here"}
            </p>
          </div>

          {/* Stats */}
          <div className="grid gap-4 mb-8 grid-cols-3">
            <div className="glass p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Link2 className="w-4 h-4 text-primary" />
                <span className="text-xs font-mono uppercase">{language === "tr" ? "Toplam Link" : "Total Links"}</span>
              </div>
              <div className="text-2xl font-mono font-bold text-primary">{totalLinks}</div>
            </div>
            <div className="glass p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <CheckCircle className="w-4 h-4 text-primary" />
                <span className="text-xs font-mono uppercase">{language === "tr" ? "Aktif Link" : "Active Links"}</span>
              </div>
              <div className="text-2xl font-mono font-bold text-primary">{activeLinks}</div>
            </div>
            <div className="glass p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Globe className="w-4 h-4 text-primary" />
                <span className="text-xs font-mono uppercase">{language === "tr" ? "Hedef Site" : "Target Sites"}</span>
              </div>
              <div className="text-2xl font-mono font-bold text-primary">{totalItems}</div>
            </div>
          </div>

          {/* Search and Pagination */}
          <div className="mb-6 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <DataPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
                onSearch={setSearchQuery}
                searchValue={searchQuery}
                searchPlaceholder={language === "tr" ? "Hedef URL veya site ara..." : "Search target URL or site..."}
                language={language}
              />
            </div>
            <Button
              variant="destructive"
              className="font-mono uppercase rounded-none flex-shrink-0 whitespace-nowrap"
              disabled={totalLinks === 0 || bulkActionLoading.clearAll}
              onClick={() => {
                const msg = language === "tr"
                  ? `Tüm linkleriniz (${totalLinks}) silinecek. Devam?`
                  : `All your links (${totalLinks}) will be deleted. Continue?`;
                if (!window.confirm(msg)) return;

                // Optimistic: panel ANINDA boşalır, bir daha geri gelmez
                setGroupedLinks([]);
                setLinkDetails({});
                setTotalLinks(0);
                setActiveLinks(0);
                setTotalItems(0);
                setTotalPages(1);
                setCurrentPage(1);
                toast.success(language === "tr" ? "Linkler silindi" : "Links deleted");

                // Fire-and-forget — backend 1sn'de 202 döner, arka planda batch'li siler
                api.delete("/my-links/clear-all").catch(() => { /* sessiz */ });
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {language === "tr" ? "TÜMÜNÜ SİL" : "DELETE ALL"}
            </Button>
          </div>

          {/* Links by Target URL */}
          {!isInitialized ? (
            <div className="text-center py-12">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <div className="text-muted-foreground font-mono">
                {language === "tr" ? "Yükleniyor..." : "Loading..."}
              </div>
            </div>
          ) : loading ? (
            <div className="text-center py-12 text-muted-foreground font-mono">
              {language === "tr" ? "Güncelleniyor..." : "Updating..."}
            </div>
          ) : groupedLinks.length === 0 ? (
            <div className="glass p-12 text-center">
              <Link2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-mono font-bold mb-2">{language === "tr" ? "Henüz link yok" : "No links yet"}</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {language === "tr" ? "Backlink satın almak için site kataloğuna gidin" : "Go to site catalog to purchase backlinks"}
              </p>
              <Link to="/sites">
                <Button className="font-mono uppercase bg-primary text-black rounded-none">
                  {language === "tr" ? "SİTE KATALOĞU" : "SITE CATALOG"}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedLinks.map((group) => (
                <div key={group.target_url} className="glass overflow-hidden">
                  {/* Group Header */}
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-white/10 rounded flex items-center justify-center flex-shrink-0">
                          <Globe className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          {editingUrl === group.target_url ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={newTargetUrl}
                                onChange={(e) => setNewTargetUrl(e.target.value)}
                                className="bg-black/50 border-white/10 rounded-none font-mono text-sm h-8"
                                placeholder="https://..."
                              />
                              <Button
                                size="sm"
                                onClick={() => saveNewTargetUrl(group.target_url)}
                                disabled={bulkActionLoading[group.target_url] === "update"}
                                className="h-8 px-2 bg-green-600 hover:bg-green-700"
                              >
                                <Save className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEditingUrl}
                                className="h-8 px-2"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="font-mono font-bold text-lg truncate flex items-center gap-2 flex-wrap">
                                <span className="truncate">{group.target_url}</span>
                                {group.has_hidden_link && (
                                  <span className="shrink-0 text-[10px] uppercase tracking-wide font-sans font-semibold px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300/90 bg-amber-500/10">
                                    {language === "tr" ? "Gizli link" : "Hidden link"}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                <span className="text-green-400">{group.total_links} {language === "tr" ? "aktif" : "active"}</span>
                                {" / "}
                                {group.total_links} {language === "tr" ? "toplam" : "total"}
                              </div>
                              {group.keywords && group.keywords.length > 0 && (
                                <div className="text-xs text-primary/90 mt-1 flex flex-wrap gap-1">
                                  <span className="text-muted-foreground">{language === "tr" ? "Keywordler:" : "Keywords:"}</span>
                                  {group.keywords.map((kw, i) => (
                                    <span key={i} className="font-mono px-1.5 py-0.5 bg-primary/10 rounded">{kw}</span>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 ml-4">
                        {/* Edit URL Button */}
                        {!editingUrl && !editingBulkKeyword && group.active_links > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditingUrl(group.target_url)}
                            className="h-8 px-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                            title={language === "tr" ? "Hedef URL değiştir" : "Change target URL"}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Bulk Keyword Edit Button */}
                        {!editingUrl && !editingBulkKeyword && group.active_links > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditingBulkKeyword(group.target_url, group.keywords)}
                            className="h-8 px-2 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20"
                            title={language === "tr" ? "Toplu keyword değiştir" : "Bulk change keyword"}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Bulk Keyword Edit Mode */}
                        {editingBulkKeyword === group.target_url && (
                          <div className="flex flex-wrap items-center gap-2">
                            {group.keywords && group.keywords.length > 1 ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {language === "tr" ? "Değiştir:" : "Replace:"}
                                </span>
                                <select
                                  value={selectedBulkOldKeyword}
                                  onChange={(e) => setSelectedBulkOldKeyword(e.target.value)}
                                  className="h-8 min-w-[120px] px-2 bg-black/50 border border-purple-500/30 rounded-md font-mono text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-purple-500"
                                >
                                  {group.keywords.map((kw, i) => (
                                    <option key={i} value={kw}>{kw}</option>
                                  ))}
                                </select>
                              </div>
                            ) : group.keywords && group.keywords.length === 1 ? (
                              <span className="text-xs text-muted-foreground font-mono px-1.5 py-1 bg-primary/10 rounded">
                                {language === "tr" ? "Keyword:" : "Keyword:"} {group.keywords[0]}
                              </span>
                            ) : null}
                            <Input
                              value={newBulkKeyword}
                              onChange={(e) => setNewBulkKeyword(e.target.value)}
                              placeholder={language === "tr" ? "Yeni keyword..." : "New keyword..."}
                              className="h-8 w-40 bg-black/50 border-purple-500/30 rounded-none font-mono text-sm"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => saveBulkKeyword(group.target_url)}
                              disabled={bulkActionLoading[group.target_url] === "bulk-keyword"}
                              className="h-8 px-2 text-emerald-400 hover:text-emerald-300"
                            >
                              {bulkActionLoading[group.target_url] === "bulk-keyword" ? (
                                <span className="animate-spin">⏳</span>
                              ) : (
                                <Save className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelEditingBulkKeyword}
                              className="h-8 px-2 text-muted-foreground hover:text-white"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        
                        {/* Bulk Cancel Button */}
                        {group.active_links > 0 && !editingBulkKeyword && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => bulkCancel(group.target_url)}
                            disabled={bulkActionLoading[group.target_url]}
                            className="h-8 px-2 text-orange-400 hover:text-orange-300 hover:bg-orange-500/20"
                            title={language === "tr" ? "Tümünü iptal et" : "Cancel all"}
                          >
                            {bulkActionLoading[group.target_url] === "cancel" ? (
                              <span className="animate-spin">⏳</span>
                            ) : (
                              <Ban className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        
                        {/* Bulk Delete Button */}
                        {(group.total_links - group.active_links) > 0 && !editingBulkKeyword && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => bulkDelete(group.target_url)}
                            disabled={bulkActionLoading[group.target_url]}
                            className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            title={language === "tr" ? "İptal edilmişleri sil" : "Delete cancelled"}
                          >
                            {bulkActionLoading[group.target_url] === "delete" ? (
                              <span className="animate-spin">⏳</span>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        
                        {/* Expand/Collapse */}
                        <button
                          onClick={() => toggleExpand(group.target_url)}
                          className="p-2 hover:bg-white/5 rounded"
                        >
                          {expandedUrls[group.target_url] ? (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Last order date */}
                    <div className="text-xs text-muted-foreground mt-2 ml-14">
                      {language === "tr" ? "Son Sipariş" : "Last Order"}: {new Date(group.last_order_date).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedUrls[group.target_url] && (
                    <div className="border-t border-white/10 p-4 bg-black/30">
                      {/* Search and Pagination Header */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                        <div className="relative flex-1 max-w-xs">
                          <Input
                            placeholder={language === "tr" ? "Site ara..." : "Search site..."}
                            value={expandedLinksSearch[group.target_url] || ""}
                            onChange={(e) => handleExpandedSearchChange(group.target_url, e.target.value)}
                            className="bg-black/50 border-white/10 rounded-none font-mono text-sm pl-8 h-8"
                          />
                          <Globe className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {language === "tr" ? "Sayfa" : "Page"} {expandedLinksPage[group.target_url] || 1} / {expandedLinksTotalPages[group.target_url] || 1}
                          {linkDetails[`${group.target_url}_total`] !== undefined && (
                            <span className="ml-2">({linkDetails[`${group.target_url}_total`]} {language === "tr" ? "toplam" : "total"})</span>
                          )}
                        </div>
                      </div>

                      {linkDetails[group.target_url] ? (
                        linkDetails[group.target_url].length > 0 ? (
                          <>
                            <div className="space-y-3">
                              {linkDetails[group.target_url].map((link, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      {/* Yayıncı domaini güvenlik gereği maskeli: tıklanır dış link yok. */}
                                      <span
                                        className="font-mono text-sm text-primary truncate flex items-center gap-1"
                                        title={link.site_name}
                                      >
                                        {link.site_name}
                                      </span>
                                      {link.hidden_link && (
                                        <span className="shrink-0 text-[10px] uppercase tracking-wide font-sans font-semibold px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300/90 bg-amber-500/10">
                                          {language === "tr" ? "Gizli link" : "Hidden link"}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                                      {editingKeyword === link.id ? (
                                        <div className="flex items-center gap-2">
                                          <span>Keyword:</span>
                                          <Input
                                            value={newKeyword}
                                            onChange={(e) => setNewKeyword(e.target.value)}
                                            className="h-6 w-32 bg-black/50 border-white/20 rounded-none font-mono text-xs px-2"
                                            placeholder="Keyword..."
                                          />
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => saveNewKeyword(link.id, group.target_url)}
                                            disabled={savingKeyword}
                                            className="h-6 w-6 p-0 text-emerald-400 hover:text-emerald-300"
                                          >
                                            <Save className="w-3 h-3" />
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={cancelEditingKeyword}
                                            className="h-6 w-6 p-0 text-muted-foreground hover:text-white"
                                          >
                                            <XCircle className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      ) : (
                                        <>
                                          Keyword: <span className="text-white">{link.anchor_text}</span>
                                          {link.status !== "cancelled" && link.status !== "expired" && (
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => startEditingKeyword(link.id, link.anchor_text)}
                                              className="h-5 w-5 p-0 text-muted-foreground hover:text-primary"
                                              title={language === "tr" ? "Keyword Düzenle" : "Edit Keyword"}
                                            >
                                              <Edit className="w-3 h-3" />
                                            </Button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4 flex-shrink-0">
                                    <div className="text-xs text-muted-foreground text-right hidden sm:block">
                                      {link.expires_at && (
                                        <div>
                                          {language === "tr" ? "Bitiş" : "Expires"}: {new Date(link.expires_at).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")}
                                        </div>
                                      )}
                                    </div>
                                    {getStatusBadge(link.status)}
                                    {!["cancelled", "failed", "expired", "refunded"].includes(link.status) && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => cancelLink(link.id, group.target_url)}
                                        disabled={deletingLinks[`cancel_${link.id}`]}
                                        className="h-8 w-8 p-0 text-orange-400 hover:text-orange-300 hover:bg-orange-500/20"
                                        title={language === "tr" ? "Linki iptal et" : "Cancel link"}
                                      >
                                        <Ban className="w-4 h-4" />
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => deleteLink(link.id, group.target_url, link.status)}
                                      disabled={deletingLinks[link.id]}
                                      className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                      title={language === "tr" ? "Linki sil" : "Delete link"}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Pagination Controls */}
                            {(expandedLinksTotalPages[group.target_url] || 1) > 1 && (
                              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-white/10">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleExpandedPageChange(group.target_url, (expandedLinksPage[group.target_url] || 1) - 1)}
                                  disabled={(expandedLinksPage[group.target_url] || 1) <= 1}
                                  className="font-mono text-xs"
                                >
                                  {language === "tr" ? "Önceki" : "Previous"}
                                </Button>
                                <span className="text-sm font-mono text-muted-foreground">
                                  {expandedLinksPage[group.target_url] || 1} / {expandedLinksTotalPages[group.target_url] || 1}
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleExpandedPageChange(group.target_url, (expandedLinksPage[group.target_url] || 1) + 1)}
                                  disabled={(expandedLinksPage[group.target_url] || 1) >= (expandedLinksTotalPages[group.target_url] || 1)}
                                  className="font-mono text-xs"
                                >
                                  {language === "tr" ? "Sonraki" : "Next"}
                                </Button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center py-4 text-muted-foreground font-mono text-sm">
                            {expandedLinksSearch[group.target_url] 
                              ? (language === "tr" ? "Sonuç bulunamadı" : "No results found")
                              : (language === "tr" ? "Link bulunamadı" : "No links found")}
                          </div>
                        )
                      ) : (
                        <div className="text-center py-4 text-muted-foreground font-mono text-sm">
                          {language === "tr" ? "Yükleniyor..." : "Loading..."}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
};

export default MyLinks;
